import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  DocumentCheckIcon,
  CloudIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { SEOHead, seoData } from '../hooks/useSEO';

const About: React.FC = () => {
  const values = [
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security Excellence',
      description: 'Proven methodologies from Fortune 500 and government environments, with expertise in NIST, DFARS, and ISO 27001 compliance frameworks.'
    },
    {
      icon: CpuChipIcon,
      title: 'Advanced AI Innovation',
      description: 'Real-time AI voice systems, OpenAI integration, and containerized microservices architecture for production-ready enterprise solutions.'
    },
    {
      icon: DocumentCheckIcon,
      title: 'Compliance & Risk Management',
      description: 'Comprehensive compliance framework implementation with proven experience in regulatory environments and audit preparation.'
    },
    {
      icon: CloudIcon,
      title: 'Infrastructure Automation',
      description: 'Enterprise-grade automation using SaltStack, CI/CD pipelines, and custom scripting for policy enforcement and optimization.'
    }
  ];


  const milestones = [
    {
      year: '2024',
      title: 'Company Founded with Enterprise Expertise',
      description: 'TyrantShield Technologies LLC established, bringing over 20 years of Fortune 500 and government cybersecurity experience to Wyoming.'
    },
    {
      year: '2024',
      title: 'Advanced AI Systems Deployment',
      description: 'Successfully deployed real-time AI voice systems with OpenAI GPT-4o integration and containerized microservices architecture.'
    },
    {
      year: '2024',
      title: 'Enterprise Security & Compliance',
      description: 'Implemented comprehensive NIST, DFARS, and ISO 27001 compliance frameworks with proven Fortune 500 methodologies.'
    },
    {
      year: '2024',
      title: 'Infrastructure Automation Excellence',
      description: 'Delivered enterprise-grade automation solutions using SaltStack, CI/CD pipelines, and large-scale network design expertise.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead {...seoData.about} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 animate-fade-in">
              About TyrantShield Technologies LLC - Cheyenne's IT Security Experts
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed animate-slide-up animation-delay-200">
              Founded in 2024 and headquartered in Cheyenne, Wyoming, TyrantShield Technologies LLC brings over two decades of enterprise cybersecurity
              and technology expertise to Wyoming businesses. We deliver proven IT solutions and methodologies from Fortune 500 and government
              environments, specializing in cybersecurity services, enterprise IT support, and comprehensive compliance
              frameworks that empower businesses across Wyoming and surrounding regions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="animate-fade-in">
              <h2 className="text-3xl font-heading font-bold text-slate-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                To be Cheyenne, Wyoming's premier IT company and cybersecurity provider, delivering enterprise-grade technology solutions with proven methodologies from Fortune 500 and government
                environments. We empower Wyoming businesses through advanced IT services, comprehensive cybersecurity frameworks,
                and compliance management systems. Our expertise spans enterprise IT support, NIST/DFARS/ISO 27001 compliance,
                network security, and technology consulting across multiple industries throughout Wyoming.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">Deploy enterprise-grade AI systems with proven security frameworks</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">Implement comprehensive compliance frameworks (NIST, DFARS, ISO 27001)</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">Deliver Fortune 500-grade infrastructure automation and security</p>
                </div>
              </div>
            </div>
            
            <div className="animate-slide-up animation-delay-200">
              <h2 className="text-3xl font-heading font-bold text-slate-900 mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                To be Wyoming's premier IT company and cybersecurity provider, recognized throughout Cheyenne and surrounding areas for delivering Fortune 500-grade
                technology solutions with proven expertise. We envision a future where every Wyoming business has access
                to enterprise-level IT support, comprehensive compliance frameworks, and advanced security
                architectures that drive growth, ensure compliance, and protect critical assets.
              </p>
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                <h3 className="font-semibold text-slate-900 mb-2">Our Commitment</h3>
                <p className="text-slate-700">
                  We're committed to delivering enterprise-grade solutions with proven methodologies from
                  Fortune 500 and government environments. Our expertise in advanced cybersecurity, AI automation,
                  and compliance frameworks ensures your business stays secure, compliant, and competitive in today's digital landscape.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Capabilities Overview */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Wyoming's Premier IT & Cybersecurity Technical Capabilities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              Explore our comprehensive portfolio of 12 enterprise-grade technical capabilities serving Cheyenne and Wyoming businesses,
              each backed by proven Fortune 500 and government experience.
            </p>
            <Link
              to="/capabilities"
              className="btn-primary inline-flex items-center space-x-2 text-lg px-8 py-4"
            >
              <span>Explore Wyoming IT Capabilities</span>
              <SparklesIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Core Values Driving Wyoming's IT Security Excellence
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              These fundamental principles guide everything we do and shape how we serve Wyoming businesses and Cheyenne area clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="card p-8 animate-slide-up" style={{ animationDelay: `${index * 150}ms` }}>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <value.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Enterprise Journey in 2024
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Bringing decades of Fortune 500 and government expertise to deliver enterprise-grade solutions
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-px h-full w-0.5 bg-blue-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} animate-fade-in`} style={{ animationDelay: `${index * 200}ms` }}>
                  {/* Timeline dot */}
                  <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  
                  {/* Content */}
                  <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="card p-6">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
                        {milestone.title}
                      </h3>
                      <p className="text-slate-700">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Why Choose Cheyenne's Premier IT Company & Cybersecurity Provider?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              We're not just another technology company – we're Wyoming's strategic IT and cybersecurity partner
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center animate-fade-in">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Enterprise-Grade Expertise</h3>
              <p className="text-blue-100">
                Over 20 years of proven experience in Fortune 500 and government environments,
                delivering advanced cybersecurity, AI automation, and compliance solutions.
              </p>
            </div>
            
            <div className="text-center animate-fade-in animation-delay-200">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Advanced Technology Solutions</h3>
              <p className="text-blue-100">
                Real-time AI voice systems, comprehensive compliance frameworks, infrastructure
                automation, and enterprise security architectures with proven methodologies.
              </p>
            </div>
            
            <div className="text-center animate-fade-in animation-delay-400">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Compliance & Security Excellence</h3>
              <p className="text-blue-100">
                Comprehensive NIST, DFARS, and ISO 27001 implementation with enterprise-grade
                security practices, risk management, and audit preparation expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Frequently Asked Questions About Our Wyoming IT Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Common questions from Cheyenne area businesses about our IT support and cybersecurity services
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                What IT services do you provide in Cheyenne, Wyoming?
              </h3>
              <p className="text-slate-700">
                We provide comprehensive IT support including cybersecurity services, network security, compliance consulting (NIST, DFARS, ISO 27001),
                enterprise infrastructure design, and 24/7 remote support for businesses throughout Cheyenne and Wyoming.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Do you serve businesses outside of Cheyenne?
              </h3>
              <p className="text-slate-700">
                Yes! While we're headquartered in Cheyenne, we provide IT services and cybersecurity solutions throughout Wyoming.
                We offer both on-site services in the Cheyenne metro area and remote support statewide.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                What makes you different from other IT companies in Wyoming?
              </h3>
              <p className="text-slate-700">
                We bring over 20 years of Fortune 500 and government cybersecurity experience to Wyoming businesses.
                Our enterprise-grade solutions include advanced AI systems, comprehensive compliance frameworks, and proven methodologies
                typically only available to large corporations.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Do you provide cybersecurity services for Wyoming energy companies?
              </h3>
              <p className="text-slate-700">
                Absolutely. We specialize in cybersecurity and compliance solutions for Wyoming's energy sector, including NERC CIP compliance,
                industrial control system security, and critical infrastructure protection with proven government and Fortune 500 methodologies.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Can you help with government contractor compliance in Wyoming?
              </h3>
              <p className="text-slate-700">
                Yes, we have extensive experience with DFARS, NIST 800-171, and other government contractor compliance requirements.
                We help Wyoming businesses achieve and maintain compliance for federal contracts and government work.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                How quickly can you respond to IT emergencies in Cheyenne?
              </h3>
              <p className="text-slate-700">
                We provide 24/7 emergency IT support with rapid response times for Cheyenne area businesses.
                Our team can provide immediate remote assistance and on-site support within hours for critical issues.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600 max-w-2xl mx-auto">
              <h3 className="font-semibold text-slate-900 mb-2">Have More Questions?</h3>
              <p className="text-slate-700 mb-4">
                Contact our Wyoming IT experts for personalized answers about your specific technology needs.
              </p>
              <Link to="/contact" className="btn-primary">
                Contact Our Cheyenne IT Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;